import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import EmersonMegaMenu from "./EmersonMegaMenu";
import { useSolutions } from "../../services/solutionsService";
import { useServices } from "../../services/servicesService";
import { generateSlug } from "../../utils/slugUtils";

function EmersonNav() {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [clickedDropdown, setClickedDropdown] = useState(null);
  const navRef = useRef(null);
  const { data: solutionsData = [] } = useSolutions();
  const { data: servicesData = [] } = useServices();

  const handleMouseEnter = (menuName) => {
    setActiveDropdown(menuName);
  };

  const handleMouseLeave = () => {
    // Only clear hover state if dropdown wasn't opened by click
    if (clickedDropdown !== activeDropdown) {
      setActiveDropdown(null);
    }
  };

  const handleClick = (e, menuName) => {
    e.preventDefault();
    // Toggle dropdown: if already open, close it; otherwise open it
    if (clickedDropdown === menuName) {
      setClickedDropdown(null);
      setActiveDropdown(null);
    } else {
      setClickedDropdown(menuName);
      setActiveDropdown(menuName);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setClickedDropdown(null);
        setActiveDropdown(null);
      }
    };

    if (clickedDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [clickedDropdown]);

  // Close dropdown when route changes
  useEffect(() => {
    setClickedDropdown(null);
    setActiveDropdown(null);
  }, [location.pathname]);

  const isActive = (link) => {
    if (link === "/") {
      return location.pathname === "/";
    }
    if (link === "#") {
      return false; // Dropdown items don't have active state
    }
    return (
      location.pathname === link || location.pathname.startsWith(link + "/")
    );
  };

  // Check if current page is a solution page
  const isSolutionPage = location.pathname.startsWith("/solutions/");
  const isServicesPage = location.pathname.startsWith("/services/");

  // Check if current page is a resources page
  const isResourcesPage =
    location.pathname.startsWith("/blogs") ||
    location.pathname.startsWith("/resources");

  // Dynamically generate menu tabs from solutions API grouped by category
  const solutionMenuTabs = useMemo(() => {
    const categoryMap = {};

    // Group solutions by category
    solutionsData.forEach((solution) => {
      const category = solution.category || "Other";
      if (!categoryMap[category]) {
        categoryMap[category] = [];
      }
      categoryMap[category].push({
        label: solution.name,
        link: `/solutions/${generateSlug(solution.name)}`,
      });
    });

    // Convert to menu tabs format
    return Object.keys(categoryMap).map((category, index) => ({
      id: category.toLowerCase().replace(/\s+/g, "-"),
      label: category,
      items: categoryMap[category],
    }));
  }, [solutionsData]);

  const servicesMenuTabs = useMemo(
    () => [
      {
        id: "services",
        label: "Engineering Services",
        items: servicesData.map((service) => ({
          label: service.name,
          link: `/services/${generateSlug(service.name)}`,
        })),
      },
    ],
    [servicesData]
  );

  // Navigation items from Nav.jsx
  const navigationItems = [
    {
      name: "Company",
      link: "/about-us",
      hasDropdown: false,
    },
    {
      name: "Solutions",
      link: "#",
      hasDropdown: true,
      menuTabs: solutionMenuTabs,
    },
    {
      name: "Services",
      link: "#",
      hasDropdown: true,
      menuTabs: servicesMenuTabs,
    },
    {
      name: "Resources",
      link: "#",
      hasDropdown: true,
      menuTabs: [
        {
          id: "resources",
          label: "Resources",
          items: [
            { label: "Blogs", link: "/blogs" },
            { label: "White Papers", link: "/whitepapers" },
            { label: "Product Documentation", link: "/product-documentation" },
          ],
        },
      ],
    },
  ];

  return (
    <nav className="emerson-nav" ref={navRef}>
      <ul className="emerson-nav-list">
        {navigationItems.map((item, index) => (
          <li
            key={index}
            className={`emerson-nav-item ${
              item.hasDropdown ? "has-mega-dropdown" : ""
            } ${activeDropdown === item.name ? "dropdown-active" : ""} ${
              clickedDropdown === item.name ? "dropdown-clicked" : ""
            } ${
              isActive(item.link) ||
              (item.name === "Solutions" && isSolutionPage) ||
              (item.name === "Services" && isServicesPage) ||
              (item.name === "Resources" && isResourcesPage)
                ? "active"
                : ""
            }`}
            onMouseEnter={() => item.hasDropdown && handleMouseEnter(item.name)}
            onMouseLeave={handleMouseLeave}
          >
            {item.hasDropdown ? (
              <a
                href="#"
                className="emerson-nav-link"
                onClick={(e) => handleClick(e, item.name)}
              >
                {item.name}
              </a>
            ) : (
              <Link to={item.link} className="emerson-nav-link">
                {item.name}
              </Link>
            )}
            {item.hasDropdown && activeDropdown === item.name && (
              <div className="emerson-mega-dropdown">
                <EmersonMegaMenu
                  menuTabs={item.menuTabs}
                  onItemClick={() => {
                    setClickedDropdown(null);
                    setActiveDropdown(null);
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default EmersonNav;
